/**
 * Виды «Нового процесса» и их формы — общие для выбора (/processes/new) и страницы формы
 * (/processes/new/[form]). Перенесено из app/(portal)/processes.tsx (16.10.2026): форма стала
 * подмаршрутом, и таблица нужна двум экранам.
 */
import type { ReactNode } from 'react'

import type { Localized } from '../../i18n'
import { PROCESS_TYPES, type ProcessType } from './registry'
import { VacationForm } from './forms/VacationForm'
import { TimeOffForm } from './forms/TimeOffForm'
import { SickLeaveForm } from './forms/SickLeaveForm'
import { BusinessTripForm } from './forms/BusinessTripForm'
import { RecruitmentForm } from './forms/RecruitmentForm'
import { FreeformForm } from './forms/FreeformForm'
import { TerminationForm } from './forms/TerminationForm'
import { ContractForm } from './forms/ContractForm'
import { HireStart } from './components/HireActions'
import { ItWorksForm } from './itWorks/ItWorksForm'

// Отдельная плитка запуска больничного — не в registry.ts PROCESS_TYPES (та же строка там уже
// занята «Отгулом»/TYPE_OF.SICK_LEAVE='off' для визуала строки заявки), поэтому подмешиваем
// её сюда локально. Иконка своя (не clock — она у «Отгула»), стоит сразу за «Отгулом».
const SICK_LEAVE_TILE: ProcessType = {
  id: 'sick',
  name: { ru: 'Больничный', en: 'Sick leave' },
  icon: 'thermometer',
  tone: 'gold',
  desc: { ru: 'Оформление больничного листа', en: 'File a sick-leave request' },
  group: 'hr',
  kw: 'больничный болезнь лист нетрудоспособности заболел sick leave illness xəstəlik vərəqəsi',
}
// «Семейные» отпуска (ТЗ-2026-012/013/014) отдельных плиток не имеют: отцовский, материнский
// и уход за ребёнком — виды в селекторе формы «Отпуск» (плитка 'vac').
// Плитка «Договор» (ТЗ-2026-015) открыта везде с 28.08.2026. Гейт оставлен выключателем: задать
// EXPO_PUBLIC_CONTRACT=0 (в .gitlab-ci.yml, variables джобы deploy:prod) — и плитка снова скроется, не трогая код.
// Из PROCESS_TYPES вид НЕ убираем — typeOf() рисует им уже существующие заявки в списке.
export const CREATE_TYPES: ProcessType[] = PROCESS_TYPES.flatMap((pt) => (pt.id === 'off' ? [pt, SICK_LEAVE_TILE] : [pt])).filter(
  (pt) => pt.id !== 'contract' || process.env.EXPO_PUBLIC_CONTRACT !== '0',
)
// Плитки в «Новом процессе» сгруппированы по разделам (заказчик, 22.09.2026): HR/Юридические/Общие,
// в этом фиксированном порядке; пустой раздел просто не рисуется.
export const GROUPS = ['hr', 'legal', 'general'] as const

/**
 * Плитка → заголовок и компонент формы. Таблица вместо цепочки тернарников: с восемью процессами
 * та стала нечитаемой, а `id in FORMS` заодно заменил ручной список «живых» плиток, который при
 * добавлении процесса забывали обновлять. Вида нет в FORMS — он в блоке «Скоро появятся».
 */
export const FORMS: Record<string, { title: Localized; Form: (p: { onDone: () => void }) => ReactNode }> = {
  fire: { title: { ru: 'Черновик увольнения', en: 'Termination draft' }, Form: TerminationForm },
  vac: { title: { ru: 'Заявка на отпуск', en: 'Vacation request' }, Form: VacationForm },
  off: { title: { ru: 'Заявка на отгул', en: 'Time-off request' }, Form: TimeOffForm },
  trip: { title: { ru: 'Оформление командировки', en: 'Business trip request' }, Form: BusinessTripForm },
  sick: { title: { ru: 'Заявка на больничный', en: 'Sick-leave request' }, Form: SickLeaveForm },
  recruitment: { title: { ru: 'Заявка на подбор персонала', en: 'Recruitment request' }, Form: RecruitmentForm },
  hire: { title: { ru: 'Приём на работу', en: 'New hire' }, Form: HireStart },
  freeform: { title: { ru: 'Документ произвольной формы', en: 'Free-form document' }, Form: FreeformForm },
  contract: { title: { ru: 'Согласование договора', en: 'Contract approval' }, Form: ContractForm },
  it_works: {
    title: { ru: 'Согласование и принятие выполненных IT-работ (аутсорс)', en: 'IT outsourcing works acceptance', az: 'Görülmüş IT işlərinin (autsorsinq) razılaşdırılması və qəbulu' },
    Form: ItWorksForm,
  },
}
